import React, { useState, useEffect } from 'react';
import { X, Share, PlusSquare, Download } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
    readonly platforms: string[];
    readonly userChoice: Promise<{
        outcome: 'accepted' | 'dismissed';
        platform: string;
    }>;
    prompt(): Promise<void>;
}

const InstallPrompt: React.FC = () => {
    const [show, setShow] = useState(false);
    const [platform, setPlatform] = useState<'ios' | 'android' | 'other'>('other');
    const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);

    useEffect(() => {
        // 1. Detect platform
        const ua = window.navigator.userAgent.toLowerCase();
        const isIOS = /iphone|ipad|ipod/.test(ua);
        const isAndroid = /android/.test(ua);

        // 2. Check if already installed (standalone mode) or in native app
        const isStandalone = window.matchMedia('(display-mode: standalone)').matches
            || (window.navigator as any).standalone
            || document.referrer.includes('android-app://');

        const isNative = window.location.origin.includes('capacitor://') ||
            window.location.origin.includes('http://localhost') ||
            !!(window as any).Capacitor;

        console.log("🕵️ PWA Detection:", { isStandalone, isNative, platform: isIOS ? 'ios' : isAndroid ? 'android' : 'other' });

        if (isStandalone || isNative) {
            console.log("🚫 Skipping InstallPrompt: already in PWA or Native mode");
            return;
        }

        // 3. Logic for iOS
        if (isIOS) {
            setPlatform('ios');
            // Show prompt after 3 seconds, but only once per session or use localStorage
            const hasSeenPrompt = localStorage.getItem('yumii_pwa_prompt_seen');
            if (!hasSeenPrompt) {
                const timer = setTimeout(() => setShow(true), 3000);
                return () => clearTimeout(timer);
            }
        }

        // 4. Logic for Android
        if (isAndroid) {
            setPlatform('android');
            const handler = (e: Event) => {
                e.preventDefault();
                setDeferredPrompt(e as BeforeInstallPromptEvent);
                const hasSeenPrompt = localStorage.getItem('yumii_pwa_prompt_seen');
                if (!hasSeenPrompt) {
                    setShow(true);
                }
            };

            window.addEventListener('beforeinstallprompt', handler);
            return () => window.removeEventListener('beforeinstallprompt', handler);
        }
    }, []);

    const handleClose = () => {
        setShow(false);
        localStorage.setItem('yumii_pwa_prompt_seen', 'true');
    };

    const handleInstallClick = async () => {
        if (deferredPrompt) {
            await deferredPrompt.prompt();
            const { outcome } = await deferredPrompt.userChoice;
            if (outcome === 'accepted') {
                setShow(false);
            }
            setDeferredPrompt(null);
        }
    };

    if (!show) return null;

    return (
        <div
            className="fixed left-4 right-4 z-[9999] animate-fade-in"
            style={{ top: 'calc(env(safe-area-inset-top, 0px) + 16px)' }}
        >
            <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 p-4 flex items-center gap-4 relative">
                <button
                    onClick={handleClose}
                    className="absolute -top-2 -right-2 bg-gray-100 rounded-full p-1 text-gray-500 hover:bg-gray-200"
                >
                    <X size={16} />
                </button>

                <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <img src="/logo-red.svg" alt="Yumii" className="w-8 h-8" />
                </div>

                <div className="flex-1">
                    <h4 className="text-sm font-bold text-gray-900 leading-tight">Installer Yumii</h4>
                    <p className="text-xs text-gray-500 mt-0.5">
                        {platform === 'ios'
                            ? "Ajoutez Yumii à votre écran d'accueil pour une expérience optimale."
                            : "Installez l'application mobile Yumii en un clic."}
                    </p>
                </div>

                {platform === 'android' ? (
                    <button
                        onClick={handleInstallClick}
                        className="bg-red-600 text-white px-4 py-2 rounded-xl text-xs font-bold flex items-center gap-2 hover:bg-red-700 active:scale-95 transition-all"
                    >
                        <Download size={14} />
                        Installer
                    </button>
                ) : (
                    <div className="flex items-center gap-2 text-gray-400">
                        <div className="flex flex-col items-center gap-1">
                            <Share size={16} className="text-blue-500" />
                            <span className="text-[10px]">Partager</span>
                        </div>
                        <span className="text-lg text-gray-200">|</span>
                        <div className="flex flex-col items-center gap-1">
                            <PlusSquare size={16} />
                            <span className="text-[10px]">Sur l'écran</span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default InstallPrompt;
